import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Users, AlertTriangle, Wrench, Map, Award } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import track from "@/lib/track";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import { useNavigate } from "react-router-dom";

export default function Operacion() {
  const [data, setData] = useState(null);
  const [bajando, setBajando] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/operacion");
        setData(data);
        track("pantalla_vista", { pantalla: "operacion" });
      } catch { setData({ error: true }); }
    })();
  }, []);

  const descargarSello = async () => {
    setBajando(true);
    try {
      const res = await api.get("/sello", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sello-${(data?.sello?.empresa || "olimpo").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tu sello se descargó. Compartilo con orgullo.");
    } catch { toast.error("No pude generar el sello ahora. Probá de nuevo en un ratito."); }
    setBajando(false);
  };

  if (!data) return <LoaderOlimpo />;

  if (!data.puertas_abiertas) {
    return (
      <div className="max-w-[720px] mx-auto w-full px-6 py-24 text-center" data-testid="operacion-bloqueada">
        <div className="mono-label text-gris mb-4" style={{ letterSpacing: "0.25em" }}>TODAVÍA NO</div>
        <h1 className="titulo text-3xl mb-4">Esto se abre cuando llegues más lejos.</h1>
        <p className="text-gris leading-relaxed mb-8">
          Con cinco procesos andando y confirmados vivos, o cien horas por mes recuperadas, se te abre el mapa de tu operación, de quién depende tu empresa y tu sello. Falta poco.
        </p>
        <button className="btn-dorado" onClick={() => nav("/")} data-testid="operacion-volver">VOLVER A MI MESA</button>
      </div>
    );
  }

  const { mapa, dependencia, sello } = data;

  return (
    <div className="max-w-[1000px] mx-auto w-full px-6 md:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mono-label text-dorado mb-3" style={{ letterSpacing: "0.25em" }}>LAS PUERTAS DEL OLIMPO</div>
        <h1 className="titulo text-4xl mb-2">Tu operación, a la vista.</h1>
        <p className="text-gris mb-12 leading-relaxed">Todo lo que armaste, junto. Queda acá para siempre.</p>

        {/* a) EL MAPA DE TU OPERACIÓN */}
        <section className="mb-16" data-testid="operacion-mapa">
          <div className="flex items-center gap-2 mb-6">
            <Map size={18} className="text-dorado" />
            <h2 className="titulo text-2xl">El mapa de tu operación</h2>
          </div>
          {mapa.length === 0 ? (
            <p className="text-gris">Todavía no hay procesos andando.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {mapa.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="rounded-xl p-5"
                  style={{ border: "1px solid #262626", background: "#0F0F0F" }}
                  data-testid={`mapa-proceso-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="titulo text-lg leading-tight">{m.proceso}</h3>
                    <span className="mono-num text-dorado shrink-0" style={{ fontSize: 15 }}>{m.horas}h/mes</span>
                  </div>
                  {m.responsables.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <Users size={13} className="text-gris" />
                      {m.responsables.map((r) => (
                        <span key={r} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(201,168,76,0.1)", color: "#C9A84C" }}>{r}</span>
                      ))}
                    </div>
                  )}
                  {m.herramientas.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <Wrench size={13} className="text-gris" />
                      {m.herramientas.map((h) => (
                        <span key={h} className="text-xs px-2 py-0.5 rounded-full" style={{ border: "1px solid #333", color: "#B9B9B9" }}>{h}</span>
                      ))}
                    </div>
                  )}
                  {m.frena.length > 0 && (
                    <div className="flex items-start gap-1.5 mt-3 pt-3" style={{ borderTop: "1px solid #1A1A1A" }}>
                      <AlertTriangle size={13} className="text-[#EC835A] mt-0.5 shrink-0" />
                      <span className="text-xs text-[#EC835A]">Se frena en: {m.frena.join(" · ")}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* b) DE QUIÉN DEPENDE TU EMPRESA */}
        <section className="mb-16" data-testid="operacion-dependencia">
          <div className="flex items-center gap-2 mb-6">
            <Users size={18} className="text-dorado" />
            <h2 className="titulo text-2xl">De quién depende tu empresa</h2>
          </div>
          {dependencia.length === 0 ? (
            <p className="text-gris">Cuando tus procesos tengan responsables cargados, lo vas a ver acá.</p>
          ) : (
            <div className="space-y-3">
              {dependencia.map((d, i) => (
                <div key={i} className="rounded-xl p-5" style={{ border: "1px solid #262626", background: "#0F0F0F" }} data-testid={`dependencia-${i}`}>
                  <p className="text-crema leading-relaxed">
                    <span className="titulo text-dorado">{d.count}</span> de tus procesos {d.count === 1 ? "pasa" : "pasan"} por <span className="text-dorado">{d.responsable}</span>. Si {d.responsable} se toma vacaciones, la operación se frena en {d.count} {d.count === 1 ? "lugar" : "lugares"} al mismo tiempo.
                  </p>
                  <p className="text-gris text-xs mt-2">{d.procesos.join(" · ")}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* c) EL SELLO */}
        <section data-testid="operacion-sello">
          <div className="flex items-center gap-2 mb-6">
            <Award size={18} className="text-dorado" />
            <h2 className="titulo text-2xl">Tu sello</h2>
          </div>
          <div className="rounded-xl p-8 text-center" style={{ border: "1px solid rgba(201,168,76,0.4)", background: "radial-gradient(circle at 50% 0%, rgba(201,168,76,0.08), #0A0A0A 70%)" }}>
            <div className="mono-label text-gris mb-6" style={{ letterSpacing: "0.3em" }}>SELLO OLIMPO · {sello.empresa}</div>
            <div className="flex items-center justify-center gap-12 mb-6">
              <div>
                <div className="titulo text-dorado" style={{ fontSize: 56 }} data-testid="sello-procesos">{sello.procesos_andando}</div>
                <div className="mono-label text-gris">PROCESOS ANDANDO</div>
              </div>
              <div>
                <div className="titulo text-dorado" style={{ fontSize: 56 }} data-testid="sello-horas">{sello.horas}</div>
                <div className="mono-label text-gris">HORAS POR MES</div>
              </div>
            </div>
            <button className="btn-dorado flex items-center gap-2 mx-auto" onClick={descargarSello} disabled={bajando} data-testid="descargar-sello">
              <Download size={15} /> {bajando ? "GENERANDO…" : "DESCARGAR MI SELLO"}
            </button>
          </div>
        </section>
      </motion.div>
    </div>
  );
}
